/**
 * Microsoft Clarity (session recordings + heatmaps). Independent of GA4:
 * GA4 keeps marketing events/conversions, Clarity only observes UX.
 *
 * The SDK injects `https://www.clarity.ms/tag/<id>` as an async script, so it
 * never blocks rendering, routing or API calls. Every call here is fail-safe:
 * a blocked/failed Clarity must never break the site.
 */
import Clarity from '@microsoft/clarity'
import { sanitizeClarityTags, type ClarityTagInput } from './clarityTags'

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void
  }
}

const PROJECT_ID = (import.meta.env.VITE_CLARITY_PROJECT_ID || '').trim()
const PROJECT_ID_RE = /^[a-z0-9]{6,20}$/i

let initCalled = false
let active = false

/**
 * Call once at app start (outside React, so StrictMode double-render cannot
 * repeat it). Repeat calls are no-ops. Dev builds stay off so local sessions
 * do not pollute the dashboard; verify with `npm run build && npm run preview`.
 */
export function initClarity(): void {
  if (initCalled || typeof window === 'undefined') return
  initCalled = true
  if (!import.meta.env.PROD || !PROJECT_ID_RE.test(PROJECT_ID)) return
  try {
    Clarity.init(PROJECT_ID)
    active = typeof window.clarity === 'function'
  } catch {
    active = false
  }
}

/** Sets allow-listed, non-PII custom tags. Unknown keys/values are dropped. */
export function setClarityTags(input: ClarityTagInput): void {
  if (!active) return
  try {
    for (const [key, value] of Object.entries(sanitizeClarityTags(input))) {
      if (value) Clarity.setTag(key, value)
    }
  } catch {
    // ignore
  }
}
